import 'styles/ModalForm.css';
import 'containers/Show/styles/ShowForm.css';

import { ToolOutlined }                           from '@ant-design/icons';
import { Dayjs }                                  from 'dayjs';
import React                                      from 'react';
import { observer }                               from 'mobx-react';
import { Button }                                 from 'antd';
import { Col }                                    from 'antd';
import { Radio }                                  from 'antd';
import { Form }                                   from 'antd';
import { CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import Row                                        from 'components/App/Row';
import { AppForm }                                from 'components/App/AppForm';
import TestSourceConnectionModel                  from 'models/TestSourceConnectionModel';
import { ApiCollection }                          from 'collections/ApiCollection';
import SourceModel                                from 'models/SourceModel';
import Input                                      from 'components/App/IntlInput';
import Select                                     from 'components/App/IntlSelect';
import FormattedMessageWrapper                    from 'components/App/FormattedMessageWrapper';
import YesOrNo                                    from 'components/App/YesOrNo';
import handleFormError                            from 'utils/handleFormError';
import FormItem                                   from 'components/App/FormItem';
import Space                                      from 'components/App/Space';
import FormItemUpload                             from 'components/App/FormItemUpload';
import ShowSourceQueryParametersForm              from 'forms/ShowSourceQueryParametersForm';
import CustomDatePicker                           from 'containers/Show/components/CustomDatePicker';
import { TIME }                                   from 'utils/constants';
import type { FormInstance }                      from 'antd';
import ShowModel                                  from 'models/ShowModel';
import ShowMediaModel                             from 'models/ShowMediaModel';
import { AppFormComponent }                       from 'components/App/AppForm';
import { messageSuccessIntl }                     from 'utils/messageIntl';

const { utcDateTimeFormat } = TIME;

interface UploadFileValue {
  uid: string;
  name: string;
  lastModified?: number;
  lastModifiedDate?: Date;
  originFileObj: any;
}

interface FormValues {
  enabled: boolean;
  exhibitorGuide: UploadFileValue[];
  externalExhibitorAccessUrl: string;
  externalSync: boolean;
  externalSyncDeadline: Dayjs | undefined;
  installationDateEnd: Dayjs | undefined;
  installationDateStart: Dayjs | undefined;
  logo: UploadFileValue[];
  map: UploadFileValue[];
  mountingAttestationOption: boolean;
  name: string;
  organizerDeadline: Dayjs | undefined;
  securityInstructions: UploadFileValue[];
  securityInstructionsOption: boolean;
  source: string;
  sourcePath: string;
  standAddDecoratorOption: boolean;
  standDocumentsOption: boolean;
  uninstallationDateEnd: Dayjs | undefined;
  uninstallationDateStart: Dayjs | undefined;
}

interface ShowFormProps {
  model: ShowModel;
  onSuccess: (show: ShowModel) => void;
}

interface ShowFormState {
  connectionOk?: boolean;
  errors: string[];
  selectedSourceIri?: string;
  testConnectionError?: any;
  testingConnection: boolean;
  withExternalSync: boolean;
  withSecurityInstructionsOption: boolean;
}

class ShowForm extends AppFormComponent<ShowFormProps> {
  public collSource = new ApiCollection(SourceModel);

  public refForm: React.RefObject<FormInstance> = React.createRef<FormInstance>();

  public state: ShowFormState = {
    connectionOk: undefined,
    errors: [],
    selectedSourceIri: undefined,
    testConnectionError: null,
    testingConnection: false,
    withExternalSync: false,
    withSecurityInstructionsOption: false,
  };

  public async componentDidMount() {
    this.setInitialValues();

    await this.collSource.list();
  }

  public componentDidUpdate(prevProps: ShowFormProps) {
    if (prevProps.model.id !== this.props.model.id) {
      this.setInitialValues();
    }
  }

  public render() {
    const { model } = this.props;
    const { errors } = this.state;

    const allErrors = [...errors];
    if (this.state.testConnectionError) {
      allErrors.push(this.state.testConnectionError);
    }

    const { withExternalSync, withSecurityInstructionsOption } = this.state;

    const selectedSource   = this.collSource.findBy('@id', this.state.selectedSourceIri) as SourceModel;
    const syncConfigLocked = !!model.externalSyncAt;
    const canTest          = withExternalSync && (!selectedSource || selectedSource.supportsConnectionTest);
    const canParams        = withExternalSync && !!model.id && (!selectedSource || selectedSource.supportsQueryParameters);

    return (
      <Form
        className="antd-form-custom-style"
        ref={this.refForm}
      >
        <Space size={12}>
          <Row>
            {allErrors.length > 0 && (
              <Col lg={24} md={24} span={24} style={{ color: '#e74c3c' }}>
                <ul>
                  {allErrors.map((error, index) => (
                    <li key={`error_${index}`}>{error}</li>
                  ))}
                </ul>
              </Col>
            )}
            <Col lg={12} md={24} sm={24} xl={12} xs={24}>
              <FormItem label="Titre du salon" name="name" required>
                <Input placeholder="Titre du salon" />
              </FormItem>
            </Col>
            <Col lg={12} md={24} sm={24} xl={12} xs={24}>
              <FormItemUpload label="Logo" name="logo" />
            </Col>
          </Row>

          <Row>
            <Col lg={24} md={24} sm={24} xl={12} xs={24}>
              <FormattedMessageWrapper id="Dates de montage :" />

              <FormItem label="Du " name="installationDateStart">
                <CustomDatePicker />
              </FormItem>

              <FormItem label="Au " name="installationDateEnd">
                <CustomDatePicker />
              </FormItem>
            </Col>
            <Col lg={24} md={24} sm={24} xl={12} xs={24}>
              <FormattedMessageWrapper id="Dates de démontage :" />

              <FormItem label="Du " name="uninstallationDateStart">
                <CustomDatePicker />
              </FormItem>

              <FormItem label="Au " name="uninstallationDateEnd">
                <CustomDatePicker />
              </FormItem>
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={24}>
              <FormItem label="Deadline organisateur " name="organizerDeadline">
                <CustomDatePicker />
              </FormItem>
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={24} sm={24} xl={12} xs={24}>
              <FormItemUpload label="Réglement" name="exhibitorGuide" />
            </Col>
            <Col lg={12} md={24} sm={24} xl={12} xs={24}>
              <FormItemUpload label="Informations salon" name="map" />
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={24}>
              <FormItem
                label="Activer les consignes de sécurité"
                name="securityInstructionsOption"
              >
                <YesOrNo
                  // buttonStyle="solid"
                  onChange={event =>
                    this.setState({ withSecurityInstructionsOption: event.target.value })
                  }
                />
              </FormItem>
            </Col>
            <Col lg={12} md={24}>
              <FormItemUpload
                disabled={!withSecurityInstructionsOption}
                label="Consignes de sécurité"
                name="securityInstructions"
                required={!!withSecurityInstructionsOption}
              />
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={24}>
              <FormItem label="Activer la gestion des documents" name="standDocumentsOption">
                <YesOrNo />
              </FormItem>
            </Col>
            <Col lg={12} md={24}>
              <FormItem label="Activer l'ajout de décorateur" name="standAddDecoratorOption">
                <YesOrNo />
              </FormItem>
            </Col>
            <Col lg={12} md={24}>
              <FormItem label="Activer l'attestation de bon montage" name="mountingAttestationOption">
                <YesOrNo />
              </FormItem>
            </Col>
          </Row>

          <Row>
            <Col lg={6} md={12}>
              <FormItem label="Salon avec passerelle" name="externalSync">
                <YesOrNo
                  disabled={!!model.externalSyncAt}
                  onChange={event => this.setState({ withExternalSync: event.target.value })}
                />
              </FormItem>
            </Col>
            <Col lg={6} md={12}>
              <FormItem label="Source" name="source" required={withExternalSync}>
                <Select
                  disabled={!withExternalSync || syncConfigLocked}
                  onChange={(value: string) => {
                    this.setState({
                      connectionOk: undefined,
                      selectedSourceIri: value,
                    });
                  }}
                  options={this.collSource.map((source: SourceModel) => ({
                    label: source.sourceName,
                    value: source.iri,
                  }))}
                  placeholder="Sélectionner une source"
                  style={{ width: '100%' }}
                />
              </FormItem>
            </Col>
            <Col lg={6} md={12}>
              <FormItem
                label={(selectedSource?.reference === 'gl_event' || selectedSource?.isSso) ? 'Identifiant salon' : 'Dossier'}
                name="sourcePath"
                required={withExternalSync}
              >
                <Input
                  disabled={!withExternalSync || syncConfigLocked}
                  placeholder={
                    (selectedSource?.reference === 'gl_event' || selectedSource?.isSso) ? 'Identifiant salon' : 'Source'
                  }
                />
              </FormItem>
            </Col>
            <Col lg={3} md={6}>
              <FormItem
                label={<span style={{ visibility: 'hidden' }}>Tester</span>}
                name="testConnection"
                required={false}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}
                >
                  <Button
                    className="btn"
                    disabled={!canTest}
                    htmlType="button"
                    key="testConnection"
                    loading={this.state.testingConnection}
                    onClick={() => this.testConnection()}
                  >
                    <FormattedMessageWrapper id="Tester" />
                  </Button>
                  {this.renderConnectionOk()}
                </div>
              </FormItem>
            </Col>
            <Col lg={3} md={6}>
              <FormItem
                label={<span style={{ visibility: 'hidden' }}>Modifier</span>}
                name="showSourceQueryParametersForm"
                required={false}
              >
                <Button
                  disabled={!canParams || !selectedSource?.id}
                  icon={<ToolOutlined />}
                  onClick={() => {
                    AppForm.open(ShowSourceQueryParametersForm, {
                      show: model,
                      source: selectedSource,
                    }, {
                      title: <FormattedMessageWrapper id="Modifier les paramètres" />,
                    });
                  }}
                />
              </FormItem>
            </Col>
          </Row>

          <Row>
            <Col lg={12} md={24}>
              <FormItem
                label="Deadline synchronisation "
                name="externalSyncDeadline"
                required={withExternalSync && !selectedSource?.isSso}
              >
                <CustomDatePicker disabled={!withExternalSync || !!selectedSource?.isSso} />
              </FormItem>
            </Col>
            <Col lg={12} md={24}>
              <FormItem
                label="Adresse de l'accès exposant"
                name="externalExhibitorAccessUrl"
                required={withExternalSync}
              >
                <Input
                  disabled={!withExternalSync}
                  placeholder="Adresse de l'accès exposant"
                />
              </FormItem>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <FormItem label="Etat" name="enabled">
                <Radio.Group buttonStyle="solid">
                  <Radio.Button data-testid="enabled-active" value>
                    <FormattedMessageWrapper id="Actif" />
                  </Radio.Button>
                  <Radio.Button data-testid="enabled-inactive" value={false}>
                    <FormattedMessageWrapper id="Inactif" />
                  </Radio.Button>
                </Radio.Group>
              </FormItem>
            </Col>
            <Col span={24}>
              <small>
                <FormattedMessageWrapper id="Lorsque le salon est actif, les contacts peuvent y accéder." />
              </small>
              <br />
              <small>
                <FormattedMessageWrapper id="Enregistrer le salon pour pouvoir saisir les informations de l'organisateur." />
              </small>
            </Col>
          </Row>
        </Space>
      </Form>
    );
  }

  public renderConnectionOk = () => {
    if (typeof this.state.connectionOk === 'undefined') {
      return null;
    }

    const icon = this.state.connectionOk ? <CheckCircleTwoTone twoToneColor="green" /> : <CloseCircleTwoTone twoToneColor="red" />;

    return (
      <div className="testConnection__iconCheckOk">
        {icon}
      </div>
    );
  };

  public submit = async () => {
    const {
      model,
      onSuccess,
    } = this.props;

    try {
      const values = await this.refForm.current?.validateFields();

      const formatDate = (date: Dayjs) => (date ? date.utc().format(utcDateTimeFormat) : undefined);

      const finalValues = {
        ...values,
        exhibitorGuide: undefined,
        externalSyncDeadline: formatDate(values.externalSyncDeadline),
        installationDateEnd: formatDate(values.installationDateEnd),
        installationDateStart: formatDate(values.installationDateStart),
        logo: undefined,
        map: undefined,
        organizerDeadline: formatDate(values.organizerDeadline),
        securityInstructions: undefined,
        source: values.source,
        uninstallationDateEnd: formatDate(values.uninstallationDateEnd),
        uninstallationDateStart: formatDate(values.uninstallationDateStart),
      };

      if (values.exhibitorGuide.length && !!values.exhibitorGuide[0].originFileObj) {
        const mediaModel = new ShowMediaModel();
        mediaModel.set('file', values.exhibitorGuide[0].originFileObj);
        const res: any = await mediaModel.save();
        finalValues.exhibitorGuide = res.data['@id'];
      }

      if (values.logo.length && !!values.logo[0].originFileObj) {
        const mediaModel = new ShowMediaModel();
        mediaModel.set('file', values.logo[0].originFileObj);
        const res: any = await mediaModel.save();
        finalValues.logo = res.data['@id'];
      }

      if (values.map.length && !!values.map[0].originFileObj) {
        const mediaModel = new ShowMediaModel();
        mediaModel.set('file', values.map[0].originFileObj);
        const res: any = await mediaModel.save();
        finalValues.map = res.data['@id'];
      }

      if (values.securityInstructions.length && !!values.securityInstructions[0].originFileObj) {
        const mediaModel = new ShowMediaModel();
        mediaModel.set('file', values.securityInstructions[0].originFileObj);
        const res: any = await mediaModel.save();
        finalValues.securityInstructions = res.data['@id'];
      }

      await model.patch(finalValues);

      messageSuccessIntl("Enregistrement réussi !");

      if (onSuccess) {
        onSuccess(model);
      }

      this.refForm.current?.resetFields();
    } catch (error: any) {
      if (error && error.errorFields) {
        // antdesign error, nothing to do
        return;
      }

      handleFormError(error, this.refForm.current);
    }
  };

  private formatTestConnectionError = (errorMessage: any) => {
    switch (errorMessage) {
      case 'EMPTY_DIRECTORY':
        return <FormattedMessageWrapper id="Dossier vide." />;
      case 'ENTRY_POINT_NOT_FOUND':
        return <FormattedMessageWrapper id="Source non-trouvée." />;
      case 'DIRECTORY_NOT_FOUND':
        return <FormattedMessageWrapper id="Dossier non-trouvé." />;
      case 'LOGIN_FAILED':
        return <FormattedMessageWrapper id="Connexion échouée." />;
      default:
        return <FormattedMessageWrapper id="Erreur lors du test de connection." />;
    }
  };

  private setInitialValues = () => {
    const { model } = this.props;

    const values: FormValues = {
      enabled: model.enabled,
      exhibitorGuide: [],
      externalExhibitorAccessUrl: model.externalExhibitorAccessUrl,
      externalSync: model.externalSync,
      externalSyncDeadline: model.externalSyncDeadlineDate || undefined,
      installationDateEnd: model.installationDateEndDate || undefined,
      installationDateStart: model.installationDateStartDate || undefined,
      logo: [],
      map: [],
      mountingAttestationOption: !!model.mountingAttestationOption,
      name: model.name,
      organizerDeadline: model.organizerDeadlineDate || undefined,
      securityInstructions: [],
      securityInstructionsOption: !!model.securityInstructionsOption,
      source: model.sourceIri || model.source,
      sourcePath: model.sourcePath,
      standAddDecoratorOption: !!model.standAddDecoratorOption,
      standDocumentsOption: !!model.standDocumentsOption,
      uninstallationDateEnd: model.uninstallationDateEndDate || undefined,
      uninstallationDateStart: model.uninstallationDateStartDate || undefined,
    };

    if (model.logoIri) {
      values.logo.push({
        lastModified: undefined,
        lastModifiedDate: undefined,
        name: model.logoMediaFileName,
        originFileObj: null,
        uid: model.logoIri,
      });
    }

    if (model.exhibitorGuideIri) {
      values.exhibitorGuide.push({
        lastModified: undefined,
        lastModifiedDate: undefined,
        name: model.exhibitorGuideMediaFileName,
        originFileObj: null,
        uid: model.exhibitorGuideIri,
      });
    }

    if (model.mapIri) {
      values.map.push({
        lastModified: undefined,
        lastModifiedDate: undefined,
        name: model.mapMediaFileName,
        originFileObj: null,
        uid: model.mapIri,
      });
    }

    if (model.securityInstructionsIri) {
      values.securityInstructions.push({
        lastModified: undefined,
        lastModifiedDate: undefined,
        name: model.securityInstructionsMediaFileName,
        originFileObj: null,
        uid: model.securityInstructionsIri,
      });
    }

    this.refForm.current?.setFieldsValue(values);

    this.setState({
      selectedSourceIri: model.sourceIri,
      withExternalSync: model.externalSync,
      withSecurityInstructionsOption: model.securityInstructionsOption,
    });
  };

  private testConnection = () => {
    this.setState({
      connectionOk: undefined,
      testingConnection: true,
    });

    const sourceFound = this.collSource.find(
        (src: SourceModel) => src.get('@id') === this.refForm.current?.getFieldValue('source'),
    );

    const sourcePath = this.refForm.current?.getFieldValue('sourcePath');

    const testShowSourceConnectionModel = new TestSourceConnectionModel({
      id: sourceFound ? sourceFound.id : undefined,
      sourcePath,
    });
    testShowSourceConnectionModel
      .save({ method: 'POST' })
      .then(() => {
        this.setState({
          connectionOk: true,
          testConnectionError: null,
        });
      })
      .catch(error => {
        this.setState({
          connectionOk: false,
          testConnectionError: this.formatTestConnectionError(
            error.connectorError.response.data['hydra:description'],
          ),
        });
      })
      .finally(() => this.setState({ testingConnection: false }));
  };
}

const ShowFormObserver = observer(ShowForm);

export default ShowFormObserver;
